/**
 * Notifications Mutation Hooks
 *
 * TanStack Query mutation hooks for notification operations. The list cache
 * holds a `NotificationsEnvelope` (FRONT-244), so optimistic updates adjust the
 * server totals alongside the items.
 */

import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { NotificationsEnvelope, RawNotification } from '@/types';
import { notificationsClient } from '../client/notifications.client';
import { queryKeys } from '../query-keys';

interface EnvelopePatch {
  notifications: RawNotification[];
  unreadDelta?: number;
  totalDelta?: number;
}

/**
 * Snapshot the cached envelope and apply an optimistic patch to items + totals.
 * Returns the snapshot for onError rollback (undefined when nothing cached).
 */
function patchListCache(
  queryClient: QueryClient,
  patch: (env: NotificationsEnvelope) => EnvelopePatch
): NotificationsEnvelope | undefined {
  const previous = queryClient.getQueryData<NotificationsEnvelope>(queryKeys.notifications.list());
  if (!previous) return undefined;

  const { notifications, unreadDelta = 0, totalDelta = 0 } = patch(previous);
  queryClient.setQueryData<NotificationsEnvelope>(queryKeys.notifications.list(), {
    ...previous,
    notifications,
    ...(previous.unread_total !== undefined
      ? { unread_total: Math.max(0, previous.unread_total + unreadDelta) }
      : {}),
    ...(previous.pagination
      ? {
          pagination: {
            ...previous.pagination,
            total: Math.max(0, previous.pagination.total + totalDelta),
          },
        }
      : {}),
  });
  return previous;
}

function rollbackListCache(queryClient: QueryClient, previous?: NotificationsEnvelope) {
  if (previous) {
    queryClient.setQueryData(queryKeys.notifications.list(), previous);
  }
}

/**
 * Hook to mark all notifications as read
 */
export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => notificationsClient.readAll(),

    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: queryKeys.notifications.list() });
      const now = new Date().toISOString();
      const previous = patchListCache(queryClient, (env) => ({
        notifications: env.notifications.map((n) => (n.read_at ? n : { ...n, read_at: now })),
        unreadDelta: -(env.unread_total ?? env.notifications.length),
      }));
      return { previous };
    },

    onError: (_error, _variables, context) => {
      rollbackListCache(queryClient, context?.previous);
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.list() });
    },
  });
}

/**
 * Hook to mark a single notification as read
 */
export function useMarkNotificationRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => notificationsClient.read(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.notifications.list() });
      const now = new Date().toISOString();
      const previous = patchListCache(queryClient, (env) => {
        const target = env.notifications.find((n) => n.id === id);
        return {
          notifications: env.notifications.map((n) => (n.id === id ? { ...n, read_at: now } : n)),
          unreadDelta: target && !target.read_at ? -1 : 0,
        };
      });
      return { previous };
    },
    onError: (_err, _id, context) => {
      rollbackListCache(queryClient, context?.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.list() });
    },
  });
}

/**
 * Hook to delete all notifications
 */
export function useDeleteAllNotifications() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => notificationsClient.deleteAll(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.list() });
    },
  });
}

/**
 * Hook to delete a single notification
 */
export function useDeleteNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => notificationsClient.delete(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.notifications.list() });
      const previous = patchListCache(queryClient, (env) => {
        const target = env.notifications.find((n) => n.id === id);
        return {
          notifications: env.notifications.filter((n) => n.id !== id),
          totalDelta: target ? -1 : 0,
          unreadDelta: target && !target.read_at ? -1 : 0,
        };
      });
      return { previous };
    },
    onError: (_err, _id, context) => {
      rollbackListCache(queryClient, context?.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.list() });
    },
  });
}
